import React, { useState } from 'react';

interface MenuItemImageProps {
  src: string;
  alt: string;
  className?: string;
  /** Use "eager" for single above-the-fold images (e.g. modal). Default "lazy" for grids/lists. */
  loading?: 'lazy' | 'eager';
  /** Hint for browser: "high" for above-the-fold, "low" for below. Speeds up first paint. */
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Width/height in px for layout stability and faster decode. Omit if size is fluid. */
  width?: number;
  height?: number;
}

/**
 * Menu item image with lazy loading and async decoding for faster page load.
 * Use for all menu item thumbnails; images load only when near viewport.
 */
const MenuItemImage: React.FC<MenuItemImageProps> = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  fetchPriority,
  width,
  height,
}) => {
  const [loaded, setLoaded] = useState(false);
  const hasValidSrc = typeof src === 'string' && src.trim() !== '';

  if (!hasValidSrc) {
    return (
      <div
        className={`bg-gray-200 ${className}`}
        style={{ width: width ?? undefined, height: height ?? undefined }}
        aria-label={alt}
        role="img"
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover bg-gray-200 ${!loaded ? 'animate-pulse' : ''} ${className}`}
      loading={loading}
      decoding="async"
      {...(fetchPriority != null && { fetchPriority })}
      {...(width != null && { width })}
      {...(height != null && { height })}
      onLoad={() => setLoaded(true)}
    />
  );
};

export default MenuItemImage;
